// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { createColumnHelper, type ExpandedState } from '@tanstack/react-table';
import { IconButton, Table } from '@shared/ui-kit/components';
import { capitalizeString } from '@shared/ui-kit/utils';
import { useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Tooltip } from '@mui/material';
import { type AdvisorRow } from '@shared/advisory/aggregation/hooks/use-advisors';

/** Payload passed when unsharing an account (subRow) with an advisor */
export type UnshareAccountPayload = Pick<
  AdvisorRow,
  'iban' | 'institutionId' | 'institutionName' | 'name'
>;

export interface AdvisorsTableProps {
  readonly data: AdvisorRow[];
  readonly initialExpanded?: ExpandedState;
  readonly onUnshareInstitution: (
    userId: string,
    sharedAccount: UnshareAccountPayload,
  ) => void;
}

const statusColorMap: Record<string, 'success' | 'warning' | 'error' | 'default'> =
  {
    active: 'success',
    pending: 'warning',
    approved: 'success',
    rejected: 'error',
    cancelled: 'error',
  };

const columnHelper = createColumnHelper<AdvisorRow>();

export const AdvisorsTable: FC<AdvisorsTableProps> = ({
  data,
  initialExpanded,
  onUnshareInstitution,
}) => {
  const { t } = useTranslation();

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: t('common.name', { ns: 'common' }),
        cell: ({ getValue, row }) => (
          <div className="flex items-center gap-x-2 truncate">
            <Table.Expander expander={row} />
            <span>{getValue()}</span>
          </div>
        ),
      }),

      columnHelper.display({
        id: 'institutionName',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const accountRow = row.original;
          const isSubRow = row.depth > 0 && accountRow.institutionName;
          return isSubRow ? (
            <Chip
              label={accountRow.institutionName}
              size="small"
              variant="outlined"
              className="!text-xs"
              color="primary"
            />
          ) : null;
        },
      }),

      columnHelper.accessor('status', {
        header: t('common.status', { ns: 'common' }),
        cell: ({ getValue }) => {
          const status = (getValue() as string) ?? '';
          if (!status) return null;

          const chipColor = statusColorMap[status] ?? 'default';
          const statusTranslations: Record<string, string> = {
            active: t('common.active', { ns: 'common' }),
            pending: t('common.pending', { ns: 'common' }),
            approved: t('common.approved', { ns: 'common' }),
            rejected: t('common.rejected', { ns: 'common' }),
            cancelled: t('common.cancelled', { ns: 'common' }),
          };
          const label = statusTranslations[status] ?? capitalizeString(status);

          return (
            <Chip
              label={label}
              variant="filled"
              size="small"
              className="!max-h-6 min-w-20"
              color={chipColor}
            />
          );
        },
      }),

      columnHelper.accessor('email', {
        header: t('common.email', { ns: 'common' }),
        cell: ({ getValue }) => {
          const email = getValue();
          if (!email) return null;
          return <a href={`mailto:${email}`}>{email}</a>;
        },
      }),

      columnHelper.display({
        id: 'options',
        enableSorting: false,
        meta: { alignment: 'end' },
        cell: ({ row }) => {
          return (
            !row.getCanExpand() && (
              <div className="flex items-center justify-end">
                <Tooltip
                  title={t('common.unshare', { ns: 'common' })}
                  placement="top"
                >
                  <span>
                    <IconButton
                      icon="Trashcan"
                      variant="icon"
                      size="lg"
                      color="main"
                      onClick={e => {
                        e.stopPropagation();
                        onUnshareInstitution(
                          row.parentId as string,
                          row.original as UnshareAccountPayload,
                        );
                      }}
                    />
                  </span>
                </Tooltip>
              </div>
            )
          );
        },
      }),
    ],
    [onUnshareInstitution, t],
  );

  return (
    <div className="overflow-x-auto">
      <Table.Grouped
        data={data}
        columns={columns}
        className="max-h-[320px]"
        getRowId={
          /* istanbul ignore next */ (row, _, parent) =>
            parent ? `${parent.id}-${row.id}` : row?.id?.toString()
        }
        initialExpanded={initialExpanded}
        initialSorting={[{ id: 'name', desc: false }]}
        data-testid="table-advisors"
      />
    </div>
  );
};
